import { FC, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { ColumnType } from 'antd/lib/table/interface';
import {
  IAdminFacility,
  IDropdownOption,
  IAdminFacilitiesFilters,
  PaginatedResponse,
  SessionStorageKey,
} from '@common/interfaces';
import { useRepository } from '@context';
import { Button, Main, Page, SearchField, Table, TableFilter, TablePagination } from '@components';
import { FilterIcon, PlusIcon } from '@assets/svg';

import css from './styles.module.scss';

const DEFAULT_PAGE_SIZE = 10;

const AdminAreaFacilitiesPage: FC = () => {
  const { facilityRepository } = useRepository();

  const params = JSON.parse(sessionStorage.getItem(SessionStorageKey.FACILITIES_LIST_PARAMS) || '{}') || {};

  const [page, setPage] = useState(params.page || 1);
  const [pageSize, setPageSize] = useState(params.pageSize || DEFAULT_PAGE_SIZE);
  const [order, setOrder] = useState(params.order || '');
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState<IDropdownOption[]>(params.countryFilter || []);

  const { data: facilities, isLoading: isDataLoading } = useQuery<PaginatedResponse<IAdminFacility>>(
    ['facilities', order, page, pageSize, search, countryFilter],
    () =>
      facilityRepository.getAdminFacilities({
        limit: pageSize,
        offset: (page - 1) * 10,
        ordering: order,
        search,
        country: countryFilter.map(({ value }) => value).join(','),
      })
  );

  const { data: filtersData, isLoading: isFiltersLoading } = useQuery<IAdminFacilitiesFilters>(
    'facilities-filters',
    () => facilityRepository.getAdminFacilitisFilters()
  );

  const clearPagination = () => {
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setOrder('');
  };

  const clearFilters = () => {
    setCountryFilter([]);
  };

  const clearAllParams = () => {
    clearPagination();
    clearFilters();
  };

  const handleSearch = (key: string) => {
    clearAllParams();
    setSearch(key);
  };

  useEffect(() => {
    sessionStorage.setItem(
      SessionStorageKey.FACILITIES_LIST_PARAMS,
      JSON.stringify({
        order,
        page,
        pageSize,
      })
    );
  }, [order, page, pageSize]);

  const columns: ColumnType<IAdminFacility>[] = [
    {
      dataIndex: 'name',
      key: 'name',
      title: 'Workshop name',
      sorter: true,
    },
    {
      dataIndex: 'city_name',
      key: 'city_name',
      title: 'City',
      sorter: true,
    },
    {
      dataIndex: 'country_name',
      key: 'country_name',
      title: 'Country',
      sorter: true,
      filterIcon: () => <FilterIcon className={`${css.filterIcon} ${countryFilter.length > 0 ? css.active : ''}`} />,
      filterDropdown: ({ confirm }) => (
        <TableFilter
          items={filtersData?.countries}
          selectedItems={countryFilter}
          onChange={(options) => {
            clearPagination();
            setCountryFilter(options);
            confirm();
          }}
        />
      ),
    },
    {
      dataIndex: 'email',
      key: 'email',
      title: 'Workshop email',
      sorter: true,
    },
    {
      dataIndex: 'depot_name',
      key: 'depot_name',
      title: 'FMS Depot ID',
      sorter: true,
      render: (_, { depot_name }) => depot_name || '-',
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: 'Workshop status',
      sorter: true,
      render: (_) => '-',
    },
  ];

  const loading = isDataLoading || isFiltersLoading;

  const results = facilities?.results || [];

  const isFiltered = countryFilter.length;

  return (
    <Main background='admin'>
      <Page>
        <div className={css.top}>
          <div className={css.title}>Workshop Facilities Management</div>
          {results.length > 0 ? (
            // TO DO: Link to
            <Button text='Create new workshop' variant='supernova' iconR={<PlusIcon />} link='' disabled />
          ) : null}
        </div>
        <section>
          <div className={css.topPanel}>
            <SearchField
              className={css.search}
              onChange={handleSearch}
              value={search}
              placeholder='Search workshop...'
            />
            {isFiltered ? <Button text='Reset filters' variant='text' onClick={clearAllParams} /> : null}
          </div>
          <Table
            className={css.table}
            columns={columns}
            data={results}
            loading={loading}
            onChangeColumnOrder={setOrder}
            variant='dark'
            tableLayout='fixed'
            onRowClick={(workshop: IAdminFacility) => {
              // navigate TO DO
            }}
          />
          <TablePagination
            page={page}
            onChangePage={setPage}
            totalCount={facilities?.count}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            variant='dark'
          />
        </section>
      </Page>
    </Main>
  );
};

export default AdminAreaFacilitiesPage;
